import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EVENT_TYPE_META, typeIcon, typeTone, formatTime } from "../lib/events";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TR_WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const EN_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * EventsCalendar — month-grid view of events.
 * Props: events (array, dates ISO), lang ('tr'|'en'), onOpen(event)
 */
export default function EventsCalendar({ events, lang, onOpen }) {
  const today = new Date();
  const [cursor, setCursor] = useState(() => startOfMonth(today));

  const monthLabel = `${(lang === "en" ? EN_MONTHS : TR_MONTHS)[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const weekdays = lang === "en" ? EN_WEEKDAYS : TR_WEEKDAYS;

  // Build 6x7 grid starting Monday before/at first day of month
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    // Monday = 0
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(first.getDate() - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  // Bucket events by yyyy-mm-dd key
  const byDay = useMemo(() => {
    const map = {};
    (events || []).forEach((ev) => {
      const d = new Date(ev.date);
      if (Number.isNaN(d.getTime())) return;
      const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[k] = map[k] || []).push(ev);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(a.date) - new Date(b.date)));
    return map;
  }, [events]);

  const prev = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const next = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const jumpToday = () => setCursor(startOfMonth(today));

  return (
    <div data-testid="events-calendar" className="surface-card overflow-hidden">
      <header className="flex items-center justify-between gap-3 px-5 md:px-7 py-5 border-b border-cream-300/60">
        <button onClick={prev} data-testid="cal-prev" className="w-9 h-9 rounded-full border border-ink/15 hover:bg-cream-200 flex items-center justify-center text-ink/70" aria-label="prev month">
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <div className="flex-1 text-center">
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-ink">{monthLabel}</h3>
        </div>
        <button onClick={jumpToday} data-testid="cal-today" className="text-xs uppercase tracking-[0.22em] text-ink/55 hover:text-ink hidden sm:inline">
          {lang === "en" ? "Today" : "Bugün"}
        </button>
        <button onClick={next} data-testid="cal-next" className="w-9 h-9 rounded-full border border-ink/15 hover:bg-cream-200 flex items-center justify-center text-ink/70" aria-label="next month">
          <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </header>

      <div className="grid grid-cols-7 px-5 md:px-7 pt-4 text-[10px] uppercase tracking-[0.22em] text-ink/50">
        {weekdays.map((w) => (
          <div key={w} className="py-1.5 text-center font-medium">{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-cream-300/40 mx-5 md:mx-7 mb-7 rounded-xl overflow-hidden border border-cream-300/60">
        {grid.map((d, idx) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const evs = byDay[key] || [];
          return (
            <div
              key={idx}
              data-testid={`cal-cell-${d.toISOString().slice(0, 10)}`}
              className={`min-h-[88px] md:min-h-[112px] p-2 md:p-2.5 flex flex-col gap-1 ${
                inMonth ? "bg-cream-50" : "bg-cream-100"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-[11px] md:text-xs ${
                    isToday
                      ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-terracotta-400 text-cream-100 font-medium"
                      : inMonth
                      ? "text-ink/85 font-medium"
                      : "text-ink/30"
                  }`}
                >
                  {d.getDate()}
                </span>
              </div>
              <div className="flex flex-col gap-1 overflow-hidden">
                {evs.slice(0, 2).map((ev) => {
                  const Icon = typeIcon(ev.type);
                  return (
                    <button
                      key={ev.id}
                      onClick={() => onOpen(ev)}
                      data-testid={`cal-event-${ev.id}`}
                      title={ev.title?.[lang] || ev.title?.tr || ev.title?.en}
                      className={`text-[10px] md:text-[11px] leading-tight px-1.5 py-1 rounded text-left truncate flex items-center gap-1 ${typeTone(ev.type)}`}
                    >
                      <Icon className="w-2.5 h-2.5 shrink-0" strokeWidth={1.8} />
                      <span className="hidden sm:inline opacity-80">{formatTime(ev.date, lang)}</span>
                      <span className="truncate">{ev.title?.[lang] || ev.title?.tr || ev.title?.en}</span>
                    </button>
                  );
                })}
                {evs.length > 2 && (
                  <button
                    onClick={() => onOpen(evs[2])}
                    className="text-[10px] text-ink/50 hover:text-ink px-1.5 py-0.5 text-left"
                  >
                    +{evs.length - 2} {lang === "en" ? "more" : "daha"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
